import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import {
	getUserOrder,
	updateEditedOrder,
} from '../../redux/actions/orderActions';
import Navbar from '../../component/navbar';
import Footer from '../../component/footer';
import {
	Box,
	Heading,
	Text,
	Image,
	Spinner,
	Center,
	Flex,
	Container,
	Stack,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Button,
	NumberInput,
	NumberInputField,
	NumberInputStepper,
	NumberIncrementStepper,
	NumberDecrementStepper,
	IconButton,
	Card,
	CardBody,
	HStack,
	Icon,
	Divider,
	Badge,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { MdEdit } from 'react-icons/md';
import Alert from '../../component/alert';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const EditOrder = ({ getUserOrder, loading, orders, updateEditedOrder }) => {
	const { order_id } = useParams();
	const navigate = useNavigate();
	const [items, setItems] = useState([]);

	useEffect(() => {
		getUserOrder(order_id);
	}, [getUserOrder, order_id]);

	useEffect(() => {
		if (orders) {
			if (items.length === 0)
				setItems(orders.map((item) => ({ ...item, isRemoved: false })));
		}
	}, [orders]);

	const matchItem = (item, productId, variantId) =>
		item.product_id === productId && (item.variant_id || null) === (variantId || null);

	const handleQuantityChange = (productId, variantId, valueAsString, valueAsNumber) => {
		setItems((currentItems) =>
			currentItems.map((item) => {
				if (matchItem(item, productId, variantId)) {
					if (valueAsString === '') return { ...item, quantity: '' };
					return { ...item, quantity: valueAsNumber, isRemoved: valueAsNumber === 0 };
				}
				return item;
			})
		);
	};

	const handleInputBlur = (productId, variantId) => {
		setItems((currentItems) =>
			currentItems.map((item) => {
				if (matchItem(item, productId, variantId) && item.quantity === '') {
					return { ...item, quantity: 0, isRemoved: true };
				}
				return item;
			})
		);
	};

	const handleRemoveItem = (productId, variantId) => {
		setItems((currentItems) =>
			currentItems.map((item) => {
				if (matchItem(item, productId, variantId)) {
					const newIsRemoved = !item.isRemoved;
					return { ...item, isRemoved: newIsRemoved, quantity: newIsRemoved ? 0 : 1 };
				}
				return item;
			})
		);
	};

	const handleSaveChanges = async () => {
		const activeItems = items.filter((item) => !item.isRemoved);
		try {
			const newOrderId = await updateEditedOrder(order_id, activeItems);
			if (newOrderId) {
				navigate('/confirm');
			}
		} catch (error) {
			console.error('Error saving edited order:', error);
		}
	};

	const subtotal = React.useMemo(() => {
		return items.reduce((acc, item) => {
			if (!item.isRemoved) {
				const quantity = parseFloat(item.quantity || '0');
				const price = parseFloat(item.price || '0');
				return acc + quantity * price;
			}
			return acc;
		}, 0);
	}, [items]);

	const hasOutOfStockActive = items.some((i) => !i.isRemoved && i.out_of_stock);
	const orderDetails = orders && orders.length > 0 ? orders[0] : null;
	const activeCount = items.filter((i) => !i.isRemoved).length;

	return (
		<Flex direction="column" minH="100vh" bg="gray.50">
			<Navbar />
			<Alert />
			<Box flex="1">
				<Container maxW="container.xl" py={8}>
					{loading ? (
						<Center minH="60vh">
							<Spinner size="xl" thickness="4px" color="blue.500" />
						</Center>
					) : orderDetails ? (
						<>
							{/* Header */}
							<HStack mb={6} spacing={3}>
								<Flex
									w="40px" h="40px"
									borderRadius="lg"
									bg="blue.50"
									align="center"
									justify="center"
								>
									<Icon as={MdEdit} boxSize={5} color="blue.500" />
								</Flex>
								<Box>
									<Heading size="lg" color="gray.800">Edit Order</Heading>
									<Text fontSize="sm" color="gray.500">Order #{orderDetails.order_id}</Text>
								</Box>
							</HStack>

							{/* Items Card */}
							<Card borderRadius="2xl" shadow="sm" overflow="hidden" mb={5}>
								<CardBody pb={0}>
									<HStack justify="space-between" mb={4}>
										<Heading size="sm" color="gray.700">
											Items ({activeCount} active)
										</Heading>
										<Text fontSize="xs" color="gray.400">
											Set qty to 0 or click delete to remove an item
										</Text>
									</HStack>
								</CardBody>
								<Box overflowX="auto">
									<Table variant="simple">
										<Thead>
											<Tr bg="gray.50">
												<Th fontSize="xs" color="gray.500">Product</Th>
												<Th fontSize="xs" color="gray.500" isNumeric>Quantity</Th>
												<Th fontSize="xs" color="gray.500" isNumeric>Unit Price</Th>
												<Th fontSize="xs" color="gray.500" isNumeric>Subtotal</Th>
												<Th fontSize="xs" color="gray.500"></Th>
											</Tr>
										</Thead>
										<Tbody>
											{items.map((item) => (
												<Tr
													key={`${item.product_id}-${item.variant_id || "base"}`}
													opacity={item.isRemoved ? 0.5 : 1}
													bg={item.isRemoved ? 'red.50' : 'white'}
													transition="all 0.2s"
												>
													<Td>
														<Flex align="center" gap={3}>
															<Image
																src={`${BASE_URL}/api/image?filename=${item.image}`}
																boxSize="48px"
																objectFit="contain"
																borderRadius="lg"
																bg="white"
																border="1px solid"
																borderColor="gray.100"
															/>
															<Text
																fontWeight="semibold"
																fontSize="sm"
																color="gray.800"
																textDecoration={item.isRemoved ? 'line-through' : 'none'}
															>
																{item.product_name}
															</Text>
															{item.variant_label && (
																<Text fontSize="xs" color="gray.500" mt={0.5}>Size: <Text as="span" fontWeight="semibold" color="purple.600">{item.variant_label}</Text></Text>
															)}
														{item.out_of_stock && (
															<Badge colorScheme="red" fontSize="xs" mt={1}>Out of Stock</Badge>
														)}
														</Flex>
													</Td>
													<Td isNumeric>
														<NumberInput
															size="sm"
															maxW="80px"
															value={item.quantity}
															min={0}
															max={99}
															onChange={(valueString, valueAsNumber) =>
																handleQuantityChange(item.product_id, item.variant_id, valueString, valueAsNumber)
															}
															isDisabled={item.isRemoved || !!item.out_of_stock}
														>
															<NumberInputField
																borderRadius="lg"
																onBlur={() => handleInputBlur(item.product_id, item.variant_id)}
															/>
															<NumberInputStepper>
																<NumberIncrementStepper />
																<NumberDecrementStepper />
															</NumberInputStepper>
														</NumberInput>
													</Td>
													<Td isNumeric>
														<Text color="gray.600" fontSize="sm">
															${parseFloat(item.price).toFixed(2)}
														</Text>
													</Td>
													<Td isNumeric>
														<Text fontWeight="semibold" color="gray.800" fontSize="sm">
															${(parseFloat(item.quantity || '0') * parseFloat(item.price)).toFixed(2)}
														</Text>
													</Td>
													<Td>
														{item.isRemoved ? (
															<Button
																size="sm"
																colorScheme="green"
																variant="outline"
																borderRadius="lg"
																onClick={() => handleRemoveItem(item.product_id, item.variant_id)}
															>
																Restore
															</Button>
														) : (
															<IconButton
																aria-label="Remove item"
																icon={<DeleteIcon />}
																colorScheme="red"
																variant="ghost"
																size="sm"
																borderRadius="lg"
																onClick={() => handleRemoveItem(item.product_id, item.variant_id)}
															/>
														)}
													</Td>
												</Tr>
											))}
										</Tbody>
									</Table>
								</Box>
							</Card>

							{/* Summary + Actions Card */}
							<Card borderRadius="2xl" shadow="sm">
								<CardBody>
									<Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
										<Box>
											<Text fontSize="sm" color="gray.400" mb={0.5}>New Order Total</Text>
											<Text fontSize="2xl" fontWeight="bold" color="teal.600">
												${subtotal.toFixed(2)}
											</Text>
										</Box>
										{hasOutOfStockActive && (
											<Text fontSize="xs" color="red.500" fontWeight="medium">
												Remove out-of-stock items before saving.
											</Text>
										)}
										<Stack direction="row" spacing={3}>
											<Button
												variant="ghost"
												borderRadius="xl"
												onClick={() => navigate(-1)}
											>
												Cancel
											</Button>
											<Button
												colorScheme="teal"
												borderRadius="xl"
												onClick={handleSaveChanges}
												isDisabled={subtotal <= 0 || hasOutOfStockActive}
												px={8}
											>
												Save Changes
											</Button>
										</Stack>
									</Flex>
								</CardBody>
							</Card>
						</>
					) : (
						<Center minH="60vh">
							<Box textAlign="center">
								<Heading size="md" color="gray.600">Order Not Found</Heading>
								<Text color="gray.400" mt={2}>This order cannot be edited.</Text>
								<Button mt={4} colorScheme="blue" borderRadius="xl" onClick={() => navigate('/order')}>
									Back to Orders
								</Button>
							</Box>
						</Center>
					)}
				</Container>
			</Box>
			<Footer />
		</Flex>
	);
};

const mapStateToProps = (state) => ({
	orders: state.order.userorder,
	loading: state.order.userloading,
});

export default connect(mapStateToProps, { getUserOrder, updateEditedOrder })(
	EditOrder
);
